import React, { useState, useEffect } from 'react';
import { Container, Typography, Card, CardContent, CardActions, Button, Box, Chip, Alert, CircularProgress } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { liveExams } from '../data/liveExams';
import { shuffleArray } from '../utils/quizUtils';

const LiveExams = () => {
    const [currentTime, setCurrentTime] = useState(Date.now());
    const [error, setError] = useState(''); // New error state
    const [loading, setLoading] = useState(false); // New loading state
    const navigate = useNavigate();

    useEffect(() => {
        const timer = setInterval(() => setCurrentTime(Date.now()), 1000); 
        return () => clearInterval(timer);
    }, []);

    const handleStartLiveExam = async (exam) => {
        setLoading(true);
        setError('');
        
        try {
            const promises = exam.sourceFiles.map(file => axios.get(`/quizzes/${file}`));
            const responses = await Promise.all(promises);
            const allQuestions = responses.flatMap(res => res.data);
            
            if (allQuestions.length === 0) {
                throw new Error("No questions found in source files.");
            }

            const shuffled = shuffleArray(allQuestions);
            const quizQuestions = shuffled.slice(0, exam.questionCount);

            navigate('/quiz', {
                state: {
                    questions: quizQuestions,
                    timeLimitMinutes: exam.durationMinutes,
                    title: exam.title,
                    isLiveExam: true,
                    liveExamId: exam.id
                }
            });
        } catch (err) {
            console.error("Failed to start live exam:", err);
            // Now the user will see why it failed
            setError(`Failed to load exam data. Missing file? (${err.message})`);
            setLoading(false);
        }
    };
    
    const activeExams = liveExams.filter(e => currentTime >= e.startTime && currentTime <= e.endTime);
    const upcomingExams = liveExams.filter(e => currentTime < e.startTime);

    return (
        <Container maxWidth="md" sx={{ py: 4 }}>
            <Typography variant="h4" align="center" gutterBottom>Live Exams</Typography>
            
            {/* Show error if one exists */}
            {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

            <Box mb={5}>
                <Typography variant="h5" gutterBottom>Active Exams</Typography>
                {activeExams.length > 0 ? activeExams.map(exam => (
                    <Card key={exam.id} variant="outlined" sx={{ mb: 2 }}>
                        <CardContent>
                            <Typography variant="h6">{exam.title}</Typography>
                            <Typography color="text.secondary">{exam.description}</Typography>
                            <Typography variant="body2" sx={{mt: 1}}>
                                Questions: {exam.questionCount} | Duration: {exam.durationMinutes} mins
                            </Typography>
                        </CardContent>
                        <CardActions>
                            <Button 
                                size="small" 
                                variant="contained" 
                                disabled={loading}
                                onClick={() => handleStartLiveExam(exam)}
                            >
                                {loading ? 'Loading...' : 'Start Now'}
                            </Button>
                        </CardActions>
                    </Card>
                )) : <Typography>No active exams at the moment.</Typography>}
            </Box>

            <Box>
                <Typography variant="h5" gutterBottom>Upcoming Exams</Typography>
                 {upcomingExams.length > 0 ? upcomingExams.map(exam => (
                    <Card key={exam.id} variant="outlined" sx={{ mb: 2 }}>
                        <CardContent>
                             <Typography variant="h6">{exam.title}</Typography>
                            <Typography color="text.secondary" sx={{mb: 1}}>
                                Starts on: {new Date(exam.startTime).toLocaleString()}
                            </Typography>
                             <Chip label="Upcoming" color="info" />
                        </CardContent>
                    </Card>
                )) : <Typography>No upcoming exams scheduled.</Typography>}
            </Box>
        </Container>
    );
};

export default LiveExams;
